import React, { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { sessionManager } from '../../config/sessionManager';
import { PermissionLevel } from '../../config/permissions';
import ShareButton from '../../components/ShareButton';

// Unified responsive game header (mobile + web) mirroring SeasonHeader pattern
const GameHeader: React.FC = () => {
  const { gameId } = useParams();
  const location = useLocation();
  const basePath = `/games/${gameId}`;
  const seasonName = sessionManager.getSeasonName();
  const gameName = sessionManager.getGameName() || 'Game';
  const shortGameName = gameName.length > 9 ? `${gameName.slice(0, 9)}...` : gameName;
  const scrollRef = useRef<HTMLDivElement>(null);
  const mobileTabsRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  // Check permissions for game menu items
  const canAccessStats = sessionManager.hasPermission('games.gameMenu.stats', PermissionLevel.VIEW);
  const canAccessDetails = sessionManager.hasPermission('games.gameMenu.details', PermissionLevel.VIEW);
  const canAccessKPIs = sessionManager.hasPermission('games.gameMenu.kpis', PermissionLevel.VIEW);

  const allTabs = [
    {
      to: `${basePath}/stats`,
      label: 'Stats',
      active: (p: string) => /\/stats$/.test(p),
      canAccess: canAccessStats,
    },
    {
      to: `${basePath}/roles`,
      label: 'Roles',
      active: (p: string) => /\/roles$/.test(p),
      canAccess: true, // Roles is always visible (not in permissions)
    },
    {
      to: `${basePath}/teams`,
      label: 'Teams',
      active: (p: string) => /\/teams$/.test(p),
      canAccess: true, // Everyone can access Teams
    },
    {
      to: `${basePath}/shots`,
      label: 'Shots',
      active: (p: string) => /\/shots$/.test(p),
      canAccess: true,
    },
    {
      to: `${basePath}/players`,
      label: 'Players',
      active: (p: string) => /\/players$/.test(p),
      canAccess: true, // Everyone can access Players
    },
    {
      to: `${basePath}/details`,
      label: 'Details',
      active: (p: string) => /\/details$/.test(p),
      canAccess: canAccessDetails,
    },
    {
      to: `${basePath}/roster`,
      label: 'Roster',
      active: (p: string) => /\/roster$/.test(p),
      canAccess: true, // Everyone can access Roster
    },
    {
      to: `${basePath}/kpis`,
      label: 'KPIs',
      active: (p: string) => /\/kpis$/.test(p),
      canAccess: canAccessKPIs,
    },
    {
      to: `${basePath}/periods`,
      label: 'Periods',
      active: (p: string) => /\/periods$/.test(p),
      canAccess: true, // Everyone can access Periods
    },
  ];

  // Filter tabs based on permissions
  const tabs = useMemo(
    () => allTabs.filter((tab) => tab.canAccess),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canAccessStats, canAccessDetails, canAccessKPIs],
  );

  const recalc = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 5);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };
  useEffect(() => {
    const scroller = mobileTabsRef.current;
    if (!scroller) return;
    const active = scroller.querySelector<HTMLElement>('[data-active="true"]');
    if (!active) return;
    const link = active.getBoundingClientRect();
    const box = scroller.getBoundingClientRect();
    if (link.left >= box.left && link.right <= box.right) return;
    scroller.scrollTo({
      left: Math.max(0, scroller.scrollLeft + (link.left - box.left) - 8),
      behavior: 'smooth',
    });
  }, [location.pathname, tabs]);

  useEffect(() => {
    recalc();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', recalc, { passive: true });
    window.addEventListener('resize', recalc);
    return () => {
      el.removeEventListener('scroll', recalc);
      window.removeEventListener('resize', recalc);
    };
  }, []);

  const scrollTabs = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: 'smooth' });
  };

  return (
    <div className="w-full bg-[#19133c] px-4 pt-4 pb-3">
      {/* Mobile: game name, divider, then the tab strip beside it */}
      <div className="md:hidden flex items-center w-full">
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-base font-bold text-white">{shortGameName}</span>
          <ShareButton contentType="game" resourceId={gameId || ''} variant="icon" currentGameId={gameId} />
        </div>
        <div className="h-6 w-px bg-white/90 mx-2 flex-shrink-0" />
        <div
          ref={mobileTabsRef}
          className="relative flex-1 min-w-0 overflow-x-auto scrollbar-hide scroll-smooth overscroll-x-contain"
        >
          <div className="flex w-max items-center gap-5 py-1">
            {tabs.map((t) => {
              const tabActive = t.active(location.pathname);
              return (
              <NavLink
                key={t.label}
                to={t.to}
                end={t.label === 'Details'}
                data-active={tabActive ? 'true' : 'false'}
                className="shrink-0"
              >
                {({ isActive }) => {
                  const active = isActive || tabActive;
                  return (
                    <span
                      className={`block whitespace-nowrap pb-1 text-sm font-semibold border-b-2 transition-colors duration-200 ${
                        active ? 'text-[#7fe526] border-[#7fe526]' : 'text-[#96c5d8] border-transparent'
                      }`}
                    >
                      {t.label}
                    </span>
                  );
                }}
              </NavLink>
              );
            })}
          </div>
        </div>
      </div>

      {/* Desktop: keep existing two-row layout */}
      <div className="hidden md:flex flex-col gap-4">
        {/* Row 1 */}
        <div className="w-full flex flex-row items-center justify-between gap-3">
          <div className="flex flex-col items-start">
            <div className="relative flex flex-row items-center gap-2">
              {seasonName && (
                <span className="hidden md:inline text-xl md:text-3xl font-bold text-white">{seasonName} -</span>
              )}
              <span className="text-xl md:text-3xl font-bold text-white">{gameName}</span>
              <ShareButton contentType="game" resourceId={gameId || ''} variant="icon" currentGameId={gameId} />
            </div>
          </div>
        </div>
        {/* Row 2: tabs with scroll helpers */}
        <div className="relative w-full">
          {showLeft && (
            <div
              aria-label="Scroll left"
              onClick={() => scrollTabs(-1)}
              className="absolute top-0 bottom-0 w-8 flex items-center justify-start pl-1 z-30 cursor-pointer select-none bg-gradient-to-r from-[#19133c] via-[#19133c]/70 to-transparent"
            >
              <span className="text-white text-3xl leading-none drop-shadow -ml-1">‹</span>
            </div>
          )}
          {showRight && (
            <div
              aria-label="Scroll right"
              onClick={() => scrollTabs(1)}
              className="absolute top-0 bottom-0 w-8 flex items-center justify-end pr-1 z-30 cursor-pointer select-none bg-gradient-to-l from-[#19133c] via-[#19133c]/70 to-transparent"
            >
              <span className="text-white text-3xl leading-none drop-shadow mr-0">›</span>
            </div>
          )}
          <div ref={scrollRef} className="overflow-x-auto scrollbar-hide relative z-10">
            <div className="flex gap-2 py-2 pr-4 md:pr-0 min-w-max md:min-w-0 md:justify-center md:flex-wrap">
              {tabs.map((t) => (
                <NavLink key={t.label} to={t.to} end={t.label === 'Details'}>
                  {({ isActive }) => {
                    const active = isActive || t.active(location.pathname);
                    return (
                      <div
                        className={`relative flex-none w-28 md:w-32 px-2 md:px-3 pt-2 pb-3 rounded-md text-sm font-semibold transition-all text-center ${
                          active ? 'bg-[#7fe526] text-black' : 'bg-[#255393] text-white'
                        }`}
                      >
                        <span>{t.label}</span>
                      </div>
                    );
                  }}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameHeader;
